/**
 * Deps Upgrade Command
 * Upgrade packages trong các ecosystem khác nhau
 */
import { Command } from 'commander';
import { checkbox, confirm } from '@inquirer/prompts';
import chalk from 'chalk';
import ora from 'ora';
import Table from 'cli-table3';
import { DepsDetectorService } from '../../services/deps-detector.service.js';
import { DepsService } from '../../services/deps.service.js';
import { DepsPhpService } from '../../services/deps-php.service.js';
import { DepsPythonService } from '../../services/deps-python.service.js';
import { checkboxTheme } from '../../utils/prompt-theme.js';
import type { 
    DepsServiceInterface, 
    DetectedProject,
    PackageInfo 
} from '../../types/deps.types.js';

interface UpgradeOptions {
    all?: boolean;
}

export function createDepsUpgradeCommand(): Command {
    return new Command('upgrade')
        .description('Upgrade packages lên version mới nhất')
        .option('--all', 'Upgrade tất cả không cần chọn')
        .action(async (options: UpgradeOptions) => {
            await handleDepsUpgrade(options);
        });
}

async function handleDepsUpgrade(options: UpgradeOptions): Promise<void> {
    const cwd = process.cwd();
    const detector = new DepsDetectorService();

    try {
        const spinner = ora('Đang phát hiện loại project...').start();
        const projects = await detector.detectProjects(cwd);

        if (projects.length === 0) {
            spinner.fail('Không tìm thấy project nào được hỗ trợ');
            console.log();
            console.log(chalk.dim('Hỗ trợ: Node.js (package.json), PHP (composer.json), Python (requirements.txt/Pipfile)'));
            process.exit(1);
        }

        spinner.succeed(`Phát hiện ${projects.length} project:`);
        for (const project of projects) {
            const icon = getEcosystemIcon(project.ecosystem);
            const label = getEcosystemLabel(project.ecosystem);
            console.log(`   ${icon} ${label} (${project.manager})`);
        }
        console.log();

        // Always upgrade all detected ecosystems
        for (const project of projects) {
            await upgradeEcosystem(project, cwd, options);
        }

        console.log();
        console.log(chalk.green('✅ Hoàn thành!'));
    } catch (error) {
        console.error(chalk.red(`\n❌ ${(error as Error).message}\n`));
        process.exit(1);
    }
}

async function upgradeEcosystem(
    project: DetectedProject,
    cwd: string,
    options: UpgradeOptions
): Promise<void> {
    const service = getService(project.ecosystem);
    const label = `${getEcosystemIcon(project.ecosystem)} ${getEcosystemLabel(project.ecosystem)}`;

    console.log(chalk.bold.cyan(`\n${'━'.repeat(80)}`));
    console.log(chalk.bold.cyan(`${label}`));
    console.log(chalk.bold.cyan('━'.repeat(80)));
    console.log();

    const spinner = ora('Đang kiểm tra packages...').start();

    let packages: PackageInfo[];
    try {
        const result = await service.check(cwd, (completed, total) => {
            spinner.text = `Đang kiểm tra packages... (${completed}/${total})`;
        });
        packages = result.packages;
    } catch (error) {
        spinner.fail('Lỗi kiểm tra packages');
        console.log(chalk.red((error as Error).message));
        return;
    }

    if (packages.length === 0) {
        spinner.succeed('Tất cả packages đã cập nhật');
        return;
    }

    spinner.succeed(`Tìm thấy ${packages.length} packages có thể nâng cấp`);
    console.log();

    displayUpgradeTable(packages);

    let selectedPackages: PackageInfo[];

    if (options.all) {
        selectedPackages = packages;
        console.log(chalk.cyan(`📋 Đã chọn tất cả ${selectedPackages.length} packages\n`));
    } else {
        try {
            const selected = await checkbox({
                message: 'Chọn packages để upgrade (↑↓ di chuyển • space chọn • a tất cả • i đảo • ⏎ xác nhận):',
                choices: packages.map(pkg => {
                    const icon = pkg.upgradeType === 'major' ? '🔴' : 
                                pkg.upgradeType === 'minor' ? '🟡' : '🟢';
                    return {
                        name: `${icon} ${pkg.name} (${pkg.current} → ${pkg.latest}) [${pkg.type}]`,
                        value: pkg.name,
                        checked: true
                    };
                }),
                pageSize: 15,
                theme: checkboxTheme,
            });

            if (selected.length === 0) {
                console.log(chalk.yellow('⏸️  Không có packages nào được chọn\n'));
                return;
            }

            selectedPackages = packages.filter(p => selected.includes(p.name));
        } catch {
            console.log(chalk.yellow('\n⏸️  Đã hủy\n'));
            return;
        }
    }

    let shouldProceed: boolean;
    try {
        shouldProceed = await confirm({
            message: `Tiến hành upgrade ${selectedPackages.length} packages? (ESC để hủy)`,
            default: true
        });
    } catch {
        console.log(chalk.yellow('\n⏸️  Đã hủy\n'));
        return;
    }

    if (!shouldProceed) {
        console.log(chalk.yellow('⏸️  Upgrade đã hủy\n'));
        return;
    }

    console.log();
    console.log(chalk.cyan(`🔧 Package manager: ${project.manager}`));
    console.log(chalk.cyan('📥 Đang upgrade...\n'));

    const commands = service.getUpgradeCommands(selectedPackages);
    
    try {
        if (commands.deps) {
            console.log(chalk.dim(`$ ${commands.deps}\n`));
        }
        if (commands.devDeps) {
            console.log(chalk.dim(`$ ${commands.devDeps}\n`));
        }

        await service.upgrade(cwd, { packages: selectedPackages });

        console.log(chalk.green(`\n✅ Đã upgrade ${selectedPackages.length} packages thành công!`));
    } catch (error) {
        console.error(chalk.red('\n❌ Lỗi khi upgrade:'));
        console.error(chalk.red((error as Error).message));

        console.log(chalk.yellow('\n💡 Bạn có thể thử upgrade thủ công:'));
        if (commands.deps) console.log(chalk.cyan(`   ${commands.deps}`));
        if (commands.devDeps) console.log(chalk.cyan(`   ${commands.devDeps}`));
        console.log();

        throw error;
    }
}

function displayUpgradeTable(packages: PackageInfo[]): void {
    const table = new Table({
        head: [
            chalk.cyan('Package'),
            chalk.cyan('Hiện tại'),
            chalk.cyan('Mới nhất'),
            chalk.cyan('Loại')
        ],
        style: {
            head: [],
            border: ['dim']
        }
    });

    for (const pkg of packages) {
        const upgradeIcon = pkg.upgradeType === 'major' ? '🔴' : 
                           pkg.upgradeType === 'minor' ? '🟡' : '🟢';
        
        table.push([
            `${upgradeIcon} ${pkg.name}`,
            chalk.dim(pkg.current),
            chalk.green(pkg.latest),
            pkg.type === 'dev' ? chalk.dim('dev') : 'dep'
        ]);
    }

    console.log(table.toString());
    console.log();
}

function getService(ecosystem: 'node' | 'php' | 'python'): DepsServiceInterface {
    switch (ecosystem) {
        case 'node':
            return new DepsService();
        case 'php':
            return new DepsPhpService();
        case 'python':
            return new DepsPythonService();
    }
}

function getEcosystemIcon(ecosystem: string): string {
    switch (ecosystem) {
        case 'node': return '📦';
        case 'php': return '🐘';
        case 'python': return '🐍';
        default: return '📦';
    }
}

function getEcosystemLabel(ecosystem: string): string {
    switch (ecosystem) {
        case 'node': return 'Node.js';
        case 'php': return 'PHP/Composer';
        case 'python': return 'Python';
        default: return ecosystem;
    }
}
